/**
 * «Автотрейдинг» — стратегия по таймеру (config/autotrading.ts).
 *
 * Экран вместо сетапа серии: настройки (ставка в % от баланса, длительность,
 * таймфрейм, темп, стоп/цель за сессию) и запуск. Сессия идёт на сервере,
 * экран опрашивает её состояние: сделки появляются по мере закрытия у
 * брокера, «Стоп» дорабатывает текущую сделку.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type {
  AutotradingPace,
  AutotradingParams,
  AutotradingSession,
  SettingsPayload,
} from '../lib/api.js';
import { money } from '../lib/format.js';
import { haptic } from '../lib/telegram.js';
import { useApp } from '../state/app.js';

const POLL_MS = 2_000;
const POLL_MAX_MS = 15_000;
const POLL_GIVE_UP = 12;

type Translate = (key: string, params?: Record<string, string | number>) => string;

const FALLBACK_OPTIONS = {
  percent: [0.5, 1, 2, 3, 5],
  durationMinutes: [15, 30, 60, 120],
  timeframeSeconds: [5, 15, 30, 60, 180, 300],
  pace: ['fast', 'normal', 'calm'] as AutotradingPace[],
  sessionLimitPercent: [0, 5, 10, 20],
  tokensPerTrade: 2,
  enabled: true,
};

function timeframeLabel(t: Translate, seconds: number): string {
  if (seconds === 0) return t('atr.value.random');
  return seconds < 60
    ? t('atr.timeframe.seconds', { value: seconds })
    : t('atr.timeframe.minutes', { value: Math.round(seconds / 60) });
}

function limitLabel(t: Translate, percent: number): string {
  return percent === 0 ? t('atr.value.off') : t('atr.percent', { value: percent });
}

function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

const signed = (value: number): string => `${value >= 0 ? '+' : '−'}$${money(Math.abs(value))}`;

function Chips<T extends number | string>({
  values,
  current,
  label,
  onPick,
  cols = 3,
}: {
  values: readonly T[];
  current: T;
  label: (value: T) => string;
  onPick: (value: T) => void;
  cols?: number;
}) {
  return (
    <div className="chip-grid" style={{ '--chip-cols': cols } as React.CSSProperties}>
      {values.map((value) => (
        <button
          key={String(value)}
          type="button"
          className={`chip ${current === value ? 'is-active' : ''}`}
          onClick={() => {
            haptic();
            onPick(value);
          }}
        >
          <span className="chip-label">{label(value)}</span>
        </button>
      ))}
    </div>
  );
}

export function AutotradingPanel({
  settings,
  onSettings,
}: {
  settings: SettingsPayload;
  onSettings: (next: SettingsPayload) => void;
}) {
  const { me, patchMe, showToast, setTab, t } = useApp();
  const options = settings.options.autotrading ?? FALLBACK_OPTIONS;
  const params = settings.current.autotrading;

  const [session, setSession] = useState<AutotradingSession | null>(null);
  const [starting, setStarting] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const watching = useRef<string | null>(null);
  const pollFailures = useRef(0);

  /** Секундная стрелка: таймер сессии и «следующая сделка через…». */
  useEffect(() => {
    if (!session || session.status !== 'running') return;
    const timer = globalThis.setInterval(() => setNow(Date.now()), 1_000);
    return () => globalThis.clearInterval(timer);
  }, [session]);

  const apply = useCallback(
    (next: AutotradingSession) => {
      if (!next || typeof next.status !== 'string' || !Array.isArray(next.trades)) {
        showToast(t('app.trade.outdated'));
        return;
      }
      setSession(next);
      if (next.status === 'running') return;
      watching.current = null;
      if (me) {
        patchMe({
          user: { ...me.user, tokens: next.tokensLeft },
          balances:
            next.market === 'real'
              ? { ...me.balances, real: next.balance }
              : { ...me.balances, demo: next.balance },
        });
      }
    },
    [me, patchMe, showToast, t],
  );

  /** Опрос идущей сессии — с растущей паузой и потолком попыток при сбоях. */
  useEffect(() => {
    if (!session || session.status !== 'running') {
      pollFailures.current = 0;
      return;
    }
    watching.current = session.sessionId;
    let cancelled = false;
    const delay = Math.min(POLL_MS * 2 ** pollFailures.current, POLL_MAX_MS);
    const timer = globalThis.setTimeout(() => {
      void api
        .autotradingSession(session.sessionId)
        .then((next) => {
          pollFailures.current = 0;
          if (!cancelled && watching.current === next.sessionId) apply(next);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const status = err instanceof ApiError ? err.status : 0;
          pollFailures.current += 1;
          if (status === 401 || status === 404 || pollFailures.current >= POLL_GIVE_UP) {
            watching.current = null;
            pollFailures.current = 0;
            setSession(null);
            if (status !== 401) showToast(t('app.error.load_failed'));
            return;
          }
          setSession((current) => (current ? { ...current } : current));
        });
    }, delay);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [session, apply, showToast, t]);

  /** Приложение открыли посреди сессии — подхватываем её. */
  useEffect(() => {
    void api
      .autotradingActive()
      .then(({ session: active }) => {
        if (active) apply(active);
      })
      .catch(() => undefined);
    // один раз при монтировании: дальше состояние ведёт опрос
  }, []);

  /** Настройка меняется оптимистично; сервер не принял — откат и тост. */
  const setParam = (key: keyof AutotradingParams, value: number | string): void => {
    const previous = params;
    const next: AutotradingParams = { ...params, [key]: value } as AutotradingParams;
    // диапазон ставки сшивается, как на сервере: «от» не бывает больше «до»
    if (key === 'minPercent' && next.maxPercent < Number(value)) next.maxPercent = Number(value);
    if (key === 'maxPercent' && next.minPercent > Number(value)) next.minPercent = Number(value);
    const withParams = (p: AutotradingParams): SettingsPayload => ({
      ...settings,
      current: { ...settings.current, autotrading: p },
    });
    onSettings(withParams(next));
    api.updateSettings({ autotrading: { [key]: value } }).catch(() => {
      onSettings(withParams(previous));
      showToast(t('app.account.save_failed'));
    });
  };

  async function start(): Promise<void> {
    if (starting) return;
    haptic('medium');
    setStarting(true);
    try {
      apply(await api.autotradingStart());
    } catch (err) {
      if (err instanceof ApiError && err.isOutOfTokens) {
        showToast(t('app.trade.no_tokens'));
        setTab('tokens');
        return;
      }
      const payload = err instanceof ApiError ? (err.payload as { error?: string; reason?: string } | null) : null;
      if (payload?.error === 'autotrading_unavailable') {
        showToast(t(`atr.stop.${payload.reason ?? 'disabled'}`));
        return;
      }
      showToast(apiMessage(err, t, 'app.trade.failed'));
      if (payload?.error === 'account_required') setTab('account');
    } finally {
      setStarting(false);
    }
  }

  async function stop(): Promise<void> {
    haptic('rigid');
    try {
      const { session: next } = await api.autotradingStop();
      if (next) setSession(next);
    } catch (err) {
      showToast(apiMessage(err, t, 'app.trade.failed'));
    }
  }

  const running = session?.status === 'running';
  const finished = session !== null && !running;

  if (session && running) {
    const left = session.endsAt - now;
    const nextIn = session.nextTradeAt ? Math.max(0, Math.ceil((session.nextTradeAt - now) / 1000)) : null;
    return (
      <div className="screen">
        <section className={`card card-lg direction-card ${session.profit >= 0 ? 'is-up' : 'is-down'}`}>
          <span className="kicker">{t('app.atr.title')}</span>
          <div className="value-xl">{clock(left)}</div>
          <span className="label">{t('app.atr.time_left')}</span>
          <span className={`pill ${session.market === 'real' ? 'pill-green' : ''}`}>
            {t(
              session.market === 'real'
                ? 'app.trade.real_badge'
                : session.live
                  ? 'app.trade.live_badge'
                  : 'app.trade.demo_badge',
            )}
          </span>
        </section>

        <section className="card">
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.atr.trades')}</div>
              <div className="victory-value">{session.trades.length}</div>
            </div>
            <div>
              <div className="label">{t('app.atr.won')}</div>
              <div className="victory-value">{session.wonTrades}</div>
            </div>
            <div>
              <div className="label">{t('app.atr.result')}</div>
              <div className={`victory-value ${session.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {signed(session.profit)}
              </div>
            </div>
          </div>

          <div className="steps-list">
            {[...session.trades].reverse().map((trade) => (
              <div key={trade.index} className={`step-row ${trade.won ? 'is-win' : 'is-loss'}`}>
                <span className="step-index">
                  {trade.pairLabel} · {trade.action === 'call' ? '▲' : '▼'} · {timeframeLabel(t, trade.timeframeSeconds)}
                </span>
                <span className="step-amount">${money(trade.amount)}</span>
                <span className="step-profit">{signed(trade.profit)}</span>
              </div>
            ))}
            {session.pending && (
              <div className="step-row is-pending">
                <span className="step-index">
                  {session.pending.pairLabel} · {session.pending.action === 'call' ? '▲' : '▼'} ·{' '}
                  {timeframeLabel(t, session.pending.timeframeSeconds)}
                </span>
                <span className="step-amount">${money(session.pending.amount)}</span>
                <span className="step-profit">
                  <span className="ai-shimmer" />
                </span>
              </div>
            )}
          </div>

          <p className="step-hint">
            {session.stopRequested
              ? t('app.atr.stopping')
              : session.claimedAt === null
                ? t('app.atr.waiting_claim')
                : session.pending
                  ? t('app.trade.waiting')
                  : nextIn !== null
                    ? t('app.atr.next_in', { seconds: nextIn })
                    : ''}
          </p>

          <button
            type="button"
            className="cta cta-secondary cta-block"
            disabled={session.stopRequested}
            onClick={() => void stop()}
          >
            {t('app.atr.stop')}
          </button>
        </section>
      </div>
    );
  }

  if (session && finished) {
    return (
      <div className="screen">
        <section className="card card-lg victory-card">
          <span className="kicker">{t('app.atr.title')}</span>
          <h2 className="card-title">{t('app.atr.finished')}</h2>
          <p className="body">{t(`atr.stop.${session.stopReason ?? 'time'}`)}</p>
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.atr.trades')}</div>
              <div className="victory-value">
                {session.trades.length} · {session.wonTrades} ✓
              </div>
            </div>
            <div>
              <div className="label">{t('app.atr.result')}</div>
              <div className={`victory-value ${session.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {signed(session.profit)}
              </div>
            </div>
            <div>
              <div className="label">{t('app.trade.balance')}</div>
              <div className="victory-value">${money(session.balance)}</div>
            </div>
            <div>
              <div className="label">{t('app.trade.tokens_spent')}</div>
              <div className="victory-value">{session.tokensSpent} 💎</div>
            </div>
          </div>
          <div className="row">
            <button type="button" className="cta cta-launch grow" onClick={() => setSession(null)}>
              {t('app.trade.again')}
            </button>
            <button type="button" className="cta cta-secondary grow" onClick={() => setTab('home')}>
              {t('app.trade.to_menu')}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="screen">
      <section className="card card-lg">
        <div className="ai-head">
          <img className="ai-emblem" src="/icons/trade.svg" alt="" width={64} height={64} />
          <div>
            <span className="kicker">{t('app.atr.title')}</span>
            <h2 className="ai-title">{t('app.atr.subtitle')}</h2>
          </div>
        </div>

        <div className="stack">
          <div>
            <div className="label">
              {t('app.atr.percent')} · {t('app.atr.from')}
            </div>
            <Chips
              values={options.percent}
              current={params.minPercent}
              label={(v) => `${v} %`}
              onPick={(v) => setParam('minPercent', v)}
              cols={5}
            />
            <div className="label">
              {t('app.atr.percent')} · {t('app.atr.to')}
            </div>
            <Chips
              values={options.percent}
              current={params.maxPercent}
              label={(v) => `${v} %`}
              onPick={(v) => setParam('maxPercent', v)}
              cols={5}
            />
          </div>

          <div>
            <div className="label">{t('app.atr.duration')}</div>
            <Chips
              values={options.durationMinutes}
              current={params.durationMinutes}
              label={(v) => t('app.atr.minutes', { value: v })}
              onPick={(v) => setParam('durationMinutes', v)}
              cols={4}
            />
          </div>

          <div>
            <div className="label">{t('app.atr.timeframe')}</div>
            <Chips
              values={[0, ...options.timeframeSeconds]}
              current={params.timeframeSeconds}
              label={(v) => timeframeLabel(t, v)}
              onPick={(v) => setParam('timeframeSeconds', v)}
              cols={4}
            />
          </div>

          <div>
            <div className="label">{t('app.atr.pace')}</div>
            <Chips
              values={options.pace}
              current={params.pace}
              label={(v) => t(`atr.pace.${v}`)}
              onPick={(v) => setParam('pace', v)}
              cols={3}
            />
          </div>

          <div>
            <div className="label">{t('app.atr.stop_loss')}</div>
            <Chips
              values={options.sessionLimitPercent}
              current={params.stopLossPercent}
              label={(v) => limitLabel(t, v)}
              onPick={(v) => setParam('stopLossPercent', v)}
              cols={4}
            />
          </div>

          <div>
            <div className="label">{t('app.atr.take_profit')}</div>
            <Chips
              values={options.sessionLimitPercent}
              current={params.takeProfitPercent}
              label={(v) => limitLabel(t, v)}
              onPick={(v) => setParam('takeProfitPercent', v)}
              cols={4}
            />
          </div>
        </div>

        <button
          type="button"
          className="cta cta-launch cta-block"
          disabled={starting || !options.enabled}
          onClick={() => void start()}
        >
          {options.enabled ? t('app.atr.start') : t('app.atr.unavailable')}
        </button>
      </section>

      <section className="card">
        <div className="row-between">
          <span className="label">{t('app.trade.tokens_available')}</span>
          <span className="pill">{me?.user.tokens ?? 0} 💎</span>
        </div>
        <p className="body">{t('app.atr.cost_note', { cost: options.tokensPerTrade })}</p>
      </section>
    </div>
  );
}
